<template>
  <a-trigger
    v-model:popup-visible="visible"
    class="custom-select-display"
    unmount-on-close
    trigger="click"
    position="bl"
    :popup-offset="4"
    animation-name="slide-dynamic-origin"
    hide-empty
    prevent-focus
    auto-fit-popup-width
    auto-fit-transform-origin
    :disabled="!isMulti"
    v-bind="$attrs"
  >
    <template v-if="isMulti" #content>
      <a-scrollbar
        class="arco-scrollbar-container arco-select-dropdown-list-wrapper"
      >
        <a-tag
          v-for="option in selectDataList"
          :key="tagKeyValue(option)"
          class="item p10"
          :closable="option.closable ?? !readonly"
          color="arcoblue"
          v-bind="contentTagProps"
          @close="handleClose(option)"
          @click.stop="handleClick(option)"
        >
          <a-tooltip :content="showLabel(option)">
            <div
              style="width: calc(100% - 25px)"
              class="text-overflow-ellipsis"
            >
              {{ showLabel(option) }}
            </div>
          </a-tooltip>
        </a-tag>
      </a-scrollbar>
    </template>
    <div class="select-display-content">
      <template v-if="selectDataList.length">
        <a-tag
          v-for="option in selectShowContent"
          :key="tagKeyValue(option)"
          class="item p10"
          color="arcoblue"
          :closable="!readonly"
          v-bind="tagProps"
          @close="handleClose(option)"
          @click.stop="handleClick(option)"
        >
          <a-tooltip :content="showLabel(option)">
            <div
              style="width: calc(100% - 25px)"
              class="text-overflow-ellipsis"
            >
              {{ showLabel(option) }}
            </div>
          </a-tooltip>
        </a-tag>
        <a-tag
          v-if="selectDataList.length > tagMaxCount"
          class="item p10"
          color="arcoblue"
        >
          {{ selectDataList.length - tagMaxCount }}+...
        </a-tag>
      </template>
    </div>
  </a-trigger>
</template>

<script lang="ts" setup>
  import { isFunction, isObject } from 'lodash';
  import { computed, PropType, ref } from 'vue';

  const props = defineProps({
    readonly: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array as PropType<Recordable[]>,
      default: () => [],
    },
    selectItem: {
      type: Object as PropType<Recordable>,
      default: null,
    },
    keyField: {
      type: String,
      default: 'ordersSn',
    },
    labelField: {
      type: String,
      default: 'label',
    },
    labelFormatter: {
      type: Function,
      default: null,
    },
    contentTagProps: {
      type: Object as PropType<Recordable>,
      default: () => ({}),
    },
    tagProps: {
      type: Object as PropType<Recordable>,
      default: () => ({}),
    },
    tagMaxCount: {
      type: Number,
      default: 2,
    },
  });

  const emits = defineEmits(['close', 'click']);
  const handleClose = (option: Recordable) => {
    emits('close', option);
  };
  const visible = ref(false);

  const selectShowContent = computed(() => {
    return selectDataList.value.slice(0, props.tagMaxCount);
  });

  const isMulti = computed(() => {
    return selectDataList.value.length > 1;
  });

  const selectDataList = computed(() => {
    const { options, selectItem, keyField } = props;
    if (selectItem && selectItem[keyField]) {
      return [selectItem];
    }
    return options;
  });

  const showLabel = (item: Recordable) => {
    if (isFunction(props.labelFormatter)) {
      return props.labelFormatter(item);
    }
    if (isObject(item)) {
      return item[props.labelField];
    }
    return item;
  };

  const handleClick = (item: Recordable) => {
    if (!props.readonly) return;
    emits('click', item);
  };

  const tagKeyValue = (item: Recordable) => {
    if (isObject(item)) {
      return item[props.keyField];
    }
    return item;
  };
</script>

<style scoped lang="less">
  .select-display-content {
    box-sizing: border-box;
    width: 100%;
    min-height: 32px;
    background-color: var(--color-fill-2);
    .item {
      width: 100%;
      margin-right: 5px;
    }
  }
</style>

<style lang="less">
  .custom-select-display {
    .item {
      width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
</style>
